<!-- @format -->
<script lang="ts">
  import type { ManageAPI } from "~/manage-api/types";
  import type { StoresKey } from "~/stores";
  import CardTitle from "../CardTitle.svelte";
  import { getStoresContext } from "~/stores";
  import { openOfferModal } from "../../../stores/offerModal.ts";
  import { formatDateRange } from "../date-utils";

  export let offer: ManageAPI.Offer;
  export let request_store_key: StoresKey;
  const { T } = getStoresContext(request_store_key);
  export let _class = "";
  export let imageStyle: string = "";
  export let borderRadius: string = "";
  export let borderThickness: string = "";
  export let borderColor: string = "";
  export let borderStyle: string = "";
  export let hoverEffect: string = ""; // translate, scale, or none
  export let showCompleteDescription = false;
  
  $: t = $T;
  // Use custom style props if provided, otherwise use site-wide CSS variables
  $: actualBorderRadius = borderRadius || "var(--offer-card-border-radius, var(--card-border-radius, 8px))";
  $: actualBorderThickness = borderThickness || "var(--offer-card-border-thickness, var(--card-border-thickness, 0))";
  $: actualBorderColor = borderColor || "var(--offer-card-border-color, var(--card-border-color, transparent))";
  $: actualBorderStyle = borderStyle || "var(--offer-card-border-style, var(--card-border-style, solid))";
  
  // Calculate hover transforms based on hover effect
  // When hoverEffect is empty, we'll use CSS variables from site-wide configurator
  $: hoverCardTransform = "";
  $: hoverImageTransform = "";
  $: {
    if (hoverEffect) {
      if (hoverEffect === "translate") {
        hoverCardTransform = "translateY(-5px)";
        hoverImageTransform = "none";
      } else if (hoverEffect === "scale") {
        hoverCardTransform = "none";
        hoverImageTransform = "scale(1.1)";
      } else if (hoverEffect === "none") {
        hoverCardTransform = "none";
        hoverImageTransform = "none";
      }
    }
  }
  
  // Build style string from computed values if imageStyle is not provided
  $: {
    if (!imageStyle) {
      const styleProps = [];
      if (actualBorderRadius) styleProps.push(`border-radius: ${actualBorderRadius}`);
      if (actualBorderThickness && actualBorderStyle && actualBorderColor) 
        styleProps.push(`border: ${actualBorderThickness} ${actualBorderStyle} ${actualBorderColor}`);
      else {
        if (actualBorderThickness) styleProps.push(`border-width: ${actualBorderThickness}`);
        if (actualBorderStyle) styleProps.push(`border-style: ${actualBorderStyle}`);
        if (actualBorderColor) styleProps.push(`border-color: ${actualBorderColor}`);
      }
      
      if (styleProps.length > 0) {
        imageStyle = styleProps.join('; ');
      }
    }
  }
  
  // Function to open the global modal
  function openModal() {
    openOfferModal(offer.id, offer, request_store_key);
  }
</script>

<button class="offer-card-button" on:click={openModal} style={hoverEffect ? `--custom-card-hover-transform: ${hoverCardTransform}; --custom-image-hover-transform: ${hoverImageTransform};` : ''}>
  <div class="offer-card {_class}">
    <div class="offer-image" style={imageStyle}>
      {#if offer.images && offer.images.length > 0}
        <img src={offer.images[0]} alt={offer.title || offer.store?.name} />
      {:else}
        <div class="offer-placeholder">
          {#if offer.store?.name}
            <span>{offer.store.name[0]}</span>
          {:else}
            <span>?</span>
          {/if}
        </div>
      {/if}
    </div>
    
    <div class="offer-content">
      <div class="offer-meta">
        <span class="validity">
          {formatDateRange(offer.validityStartDate, offer.endDate)}
        </span>
        <span class="store-name">{offer.store?.name}</span>
      </div>
      <CardTitle title={offer.title || ""} _class={_class} />
      {#if showCompleteDescription}
        <div>{offer.description1}</div>
        <div>{offer.description2}</div>
        {#if offer.lowestPriceInXDays}
          <div>{t("offers.lowest-price-30-days")} {offer.lowestPriceInXDays}</div>
        {/if}
      {/if}
    </div>
  </div>
</button>

<style lang="scss">
  .offer-card-button {
    width: 100%;
    height: 100%;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-align: left;
    display: block;
    transition: transform 0.2s ease-in-out;
    
    &:hover {
      @media (min-width: 768px) {
        transform: var(--custom-card-hover-transform, var(--card-hover-transform, none));
        
        img, .offer-placeholder {
          transform: var(--custom-image-hover-transform, var(--image-hover-transform, none));
        }
      }
    }
  }

  .offer-card {
    overflow: hidden;
    height: 100%;

    &.detailed {
      text-align: left;
    }
  }

  .offer-image {
    width: 100%;
    height: auto;
    overflow: hidden;
    position: relative;
    aspect-ratio: 1/1;

    @media (max-width: 1200px) {
      height: auto;
      &.rectangle-image {
        height: 250px;
      }
    }
    
    @media (max-width: 992px) {
      height: auto;
      &.rectangle-image {
        height: 220px;
      }
    }
    
    @media (max-width: 768px) {
      height: auto;
      &.rectangle-image {
        height: 200px;
      }
    }
    
    @media (max-width: 475px) {
      width: 100%;
      height: auto;
    }

    img, .offer-placeholder {
      width: 100%;
      height: 100%;
      -webkit-object-fit: cover;
      object-fit: cover;
      transform: scale(1.01); /* Slight scale to prevent edge artifacts in Safari */
      transition: transform 0.3s ease;
    }
    
    .offer-placeholder {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #f5f5f5;
      
      span {
        font-size: 48px;
        font-weight: bold;
        color: #000;
      }
    }
  }

  .offer-content {
    padding: 1rem 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    flex: 1;

    :global(.title-container) {
      height: 36px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      display: block;
    }
  }

  .offer-meta {
    font-family: var(--liferay-font-family-base);
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.875rem;
    color: #666;
  }

  .validity {
    font-size: 0.875rem;
  }

  .store-name {
    text-align: right;
    font-weight: 500;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Detailed view styles */
  .content-section {
    text-align: left;
    padding: 10px;
    color: #000;
  }

  .content-section .store-name {
    font-size: 24px;
    font-weight: 600;
    text-align: left;
    max-width: 100%;
    margin: 0.5rem 0;
  }

  .description {
    font-size: 16px;
    font-weight: 400;
    margin: 0.5rem 0;
  }

  .offer-validity {
    font-size: 14px;
    margin: 0.5rem 0;
  }
</style>
